import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { regex, SelectInput, TextInput, useNotify } from 'react-admin';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axiosApiInstance from 'src/api/axiosConfig';
import { palette, Theme } from 'src/Theme';
import { ReactComponent as UsersIcon } from '../../../../assets/users.svg';
import { ReactComponent as DeleteIcon } from 'src/assets/trash-can.svg';
import { EducationLevelGradeRange } from 'src/constants/subjects';
import { useScheduleCalendarContext } from '../../common/context/CalendarContext';
import { UserRoleToLessonTypeMap } from '../../common/const/ScheduleCalendar.const';
import { ShsButton, ShsSelectInput, ShsTextInput, ShsToggle } from 'src/components/common';
import { ConsultationType, INVALID_URL_MESSAGE, URL_REGEX } from 'src/constants';
import { fetchCenterData, fetchChildrenData, fetchEmployeesData, fetchLessonData } from '../../common/api';
import { LessonType } from '../../common/enums/lesson.enum';
import { ScheduleManager } from 'src/managers/Schedule/ScheduleManager';
import { ScheduleCalendarEvent } from './ScheduleCalendar.types';
import { UserRole } from 'src/auth/types';
import WithRole from 'src/components/common/WithRole';

interface EditCalendarEventProps {
    open: boolean;
    onClose: () => void;
    dialogEvent: ScheduleCalendarEvent;
    refetchEvents: () => void;
}

const StyledInput = styled(TextInput)({
    '.MuiInputBase-input': {
        height: '20px',
        '&.Mui-disabled': {
            background: '#F3F4F6'
        }
    }
});

const StyledSelectInput = styled(SelectInput)({
    '& .MuiFormHelperText-root': {
        display: 'none'
    },
    '& fieldset legend': {
        display: 'none'
    }
});

const StyledUsersIcon = styled(UsersIcon)({
    width: '16px',
    height: '16px',
    stroke: '#111928'
});

const StyledDialog = styled(Dialog)({
    '& .MuiPaper-root': {
        padding: '24px 32px',
        margin: 0,
        width: '750px',
        minHeight: '750px',
        maxHeight: '1250px',
        gap: '12px',
        borderRadius: '20px'
    }
});

const StyledEditActionButton = styled(ShsButton)({
    padding: 0,
    minWidth: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    '.MuiButton-icon': {
        margin: 0
    },
    ':hover': {
        filter: 'brightness(0.95)'
    }
});

const StyledDeleteIcon = styled(DeleteIcon)({
    fill: Theme.palette.primary.main,
    cursor: 'pointer',
    ':hover': {
        filter: 'brightness(0.95)'
    }
});

const ChildrenGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '16px auto fit-content(80px) minmax(20px, 80px)',
    gridColumnGap: '10px',
    alignItems: 'baseline'
});

const NewChildGrid = styled(Box)({
    minHeight: 'calc(40px + 8px + 8px)', // shsselect height + margins
    gridColumn: '2/3',
    justifySelf: 'left'
});

const ChildItemControlsBox = styled(Box)({
    marginLeft: 'auto',
    width: '100%',
    display: 'flex',
    gap: '4px',
    justifyContent: 'flex-end'
});

const title = (title?: string, consultatioType?: string) => {
    if (consultatioType) {
        return (
            <>{`Редагування уроку: ${title} (${ConsultationType[consultatioType as keyof typeof ConsultationType]})`}</>
        );
    }
    return `Редагування уроку: ${title} `;
};

const getUserRole = (type: LessonType, isForeignLanguageTeacher: boolean, isSpecialLessons: boolean): UserRole => {
    if (isForeignLanguageTeacher) {
        return UserRole.FOREIGN_LANGUAGE_TEACHER;
    }
    if (isSpecialLessons) {
        return UserRole.SPECIAL_EDUCATION_TEACHER;
    }
    return Object.entries(UserRoleToLessonTypeMap).find(([_, value]) => value === type)?.[0] as UserRole;
};

export const ShsCalendarEventEditDialog = ({ open, onClose, dialogEvent, refetchEvents }: EditCalendarEventProps) => {
    const [addChildMode, setAddChildMode] = useState(false);
    const [eventChildren, setEventChildren] = useState<any[]>([]);
    const [availableChildren, setAvailableChildren] = useState<any[]>([]);
    const dialogForm = useForm();
    const newChildId = useWatch({ control: dialogForm.control, name: 'newChild.id', defaultValue: null });
    const { isValid, isDirty } = useFormState({ control: dialogForm.control });
    const { calendarData, calendarConfig, setCalendarData } = useScheduleCalendarContext();
    const notify = useNotify();

    const { isFetching: isFetchingLessonData, data: lessonData } = useQuery([dialogEvent.id], () =>
        fetchLessonData(dialogEvent!.id)
    );

    const isForeignLanguageTeacher = lessonData?.subject?.roles?.includes(UserRole.FOREIGN_LANGUAGE_TEACHER);
    const isSpecialLessons = lessonData?.employee?.roleDescription === 'Вчитель спеціальної освіти'; // Temporary hack until a workaround for special lessons is implemented

    const { data: centerData } = useQuery(
        [calendarData.educationCenter?.id],
        () => fetchCenterData(calendarData.educationCenter!.id),
        {
            enabled: open && !!calendarData.educationCenter
        }
    );

    const { data: childrenData = [] } = useQuery(
        [dialogEvent.id, lessonData?.id],
        () =>
            fetchChildrenData(
                EducationLevelGradeRange[calendarConfig.educationLevelGroup],
                lessonData.subject.id,
                new Date(lessonData.lessonDate),
                calendarData.educationCenter?.id,
                calendarData.hospital?.id
            ),
        {
            enabled: open && !isFetchingLessonData && !!lessonData
        }
    );

    const getUpdatedChildren = () => {
        const formChildren = dialogForm.getValues('children');
        const updatedChildren = eventChildren?.map((eventChild: any) => {
            const formChildData = formChildren?.find((child: any) => child.id === eventChild.id);
            let childId = eventChild.id;
            if (childrenData) {
                const childData = childrenData?.find((child) => child.id === eventChild.id);
                childId = childData!.id;
            }

            return {
                childId,
                online: formChildData?.online
            };
        });
        return updatedChildren;
    };

    const updatedChildren = getUpdatedChildren();
    const isChildrenOnline = updatedChildren?.some((child: any) => child.online);
    const isChildrenOffline = updatedChildren?.some((child: any) => !child.online);

    const { data: employeesData = [] } = useQuery(
        [lessonData],
        () =>
            fetchEmployeesData(
                EducationLevelGradeRange[calendarConfig.educationLevelGroup],
                getUserRole(lessonData.type, isForeignLanguageTeacher, isSpecialLessons),
                lessonData.subject.id,
                calendarConfig.range.start,
                calendarData.educationCenter?.id,
                isForeignLanguageTeacher || isSpecialLessons
            ),
        {
            enabled: open && !!lessonData
        }
    );

    const availableClassrooms = useMemo(
        () =>
            centerData?.classroomLocations
                ?.filter(
                    (classroom: any) =>
                        classroom.targetGradeRange.includes(
                            EducationLevelGradeRange[calendarConfig.educationLevelGroup]
                        ) && classroom.hospitalId === calendarData.hospital?.id
                )
                .map((classroom: any) => ({
                    id: classroom.id,
                    name: classroom.name
                })) || [],
        [calendarData, calendarConfig]
    );

    useEffect(() => {
        if (!open) {
            dialogForm.reset();
            setAddChildMode(false);
            setEventChildren([]);
            setAvailableChildren([]);
        }
    }, [open]);

    useEffect(() => {
        if (!lessonData?.children) return;

        const lessonChildrenMap = new Map(lessonData.children.map((lc: any) => [lc.childId, lc.online]));

        setEventChildren(
            childrenData
                .filter((child: any) => lessonChildrenMap.has(child.id))
                .map((child: any) => ({
                    ...child,
                    online: lessonChildrenMap.get(child.id)
                }))
        );
    }, [childrenData, lessonData?.children]);

    useEffect(() => {
        eventChildren.forEach((child, index) => {
            dialogForm.setValue(`children.${index}.id`, child.id, { shouldDirty: false });
            dialogForm.setValue(`children.${index}.name`, `${child.name} ${child.surname}`, { shouldDirty: false });
            dialogForm.setValue(`children.${index}.online`, child.online, { shouldDirty: false });
        });

        setAvailableChildren(
            childrenData.filter(
                (child) => !eventChildren.find((eventChild: any) => eventChild.id === child.id) && child.lessonCount > 0
            )
        );
    }, [eventChildren]);

    useEffect(() => {
        if (lessonData) {
            dialogForm.setValue('classroomId', lessonData.classroom?.id || 'other', { shouldDirty: false });
            dialogForm.setValue('meetingLink', lessonData.meetingLink, { shouldDirty: false });
            dialogForm.setValue('notes', lessonData.notes, { shouldDirty: false });
            dialogForm.setValue('employeeId', lessonData.employeeId, { shouldDirty: false });
        }
    }, [lessonData]);

    useEffect(() => {
        if (!isChildrenOffline) {
            dialogForm.setValue('classroomId', null);
        } else if (lessonData) {
            dialogForm.setValue('classroomId', lessonData.classroom?.id || 'other');
        }
    }, [isChildrenOffline, lessonData]);

    function handleChildDelete(childId: number) {
        setEventChildren((children) => children.filter((child) => child.id !== childId));
        if (!isDirty) {
            dialogForm.setValue('notes', lessonData.notes, { shouldDirty: true }); //hack to trigger form and make isDirty: true
        }
    }

    function handleChildAdd() {
        const newChild = availableChildren.find((child) => child.id === dialogForm.getValues()['newChild']['id']);

        if (!newChild) {
            throw new Error('Child not found');
        }

        setEventChildren((children) => [...children, newChild]);
        dialogForm.resetField('newChild');
        setAddChildMode(false);
    }

    async function handleLessonDelete(event: ScheduleCalendarEvent) {
        await axiosApiInstance.delete(`lessons/${event.id}`);

        setCalendarData((data) => ({
            ...data,
            deletedEvent: event
        }));

        onClose();
    }

    async function handleLessonSave() {
        const updatedChildren = getUpdatedChildren();

        const updatedClassroomId = dialogForm.getValues('classroomId');
        const updatedEmployeeId = dialogForm.getValues('employeeId');
        const updatedMeetingLink = dialogForm.getValues('meetingLink');
        const updatedNotes = dialogForm.getValues('notes');

        const updatedEvent = {
            ...lessonData,
            children: updatedChildren,
            classroomId: updatedClassroomId === 'other' ? null : updatedClassroomId,
            employeeId: updatedEmployeeId,
            notes: updatedNotes,
            meetingLink: updatedMeetingLink
        };

        await axiosApiInstance.put(`lessons/update`, updatedEvent);
        refetchEvents();
        onClose();
    }

    const onSaveClick = async () => {
        try {
            await handleLessonSave();
        } catch (error: any) {
            console.error(error);
            notify(error.message, { type: 'error' });
        }
    };
    const consultatioType = lessonData?.subject?.consultationType;

    const notesMaxLength = 255;

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogTitle sx={{ padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <>
                    {title(dialogEvent?.title, consultatioType)}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </>
            </DialogTitle>

            <DialogContent sx={{ padding: 0, display: 'flex' }}>
                <Box sx={{ width: '100%' }}>
                    <FormProvider {...dialogForm}>
                        <Typography fontSize="16px" fontWeight={600} marginBottom="12px">
                            Список учнів
                        </Typography>
                        <ChildrenGrid>
                            {eventChildren.map((child: any, index: number) => (
                                <Fragment key={child.id}>
                                    <input type="hidden" {...dialogForm.register(`children.${index}.id`)} />
                                    <StyledUsersIcon />
                                    <StyledInput
                                        source={`children.${index}.name`}
                                        readOnly
                                        label={false}
                                        helperText={false}
                                    />
                                    <ShsToggle source={`children.${index}.online`} label={'Онлайн'} />
                                    <ChildItemControlsBox>
                                        <StyledDeleteIcon onClick={() => handleChildDelete(child.id)} />
                                    </ChildItemControlsBox>
                                </Fragment>
                            ))}
                            {addChildMode ? (
                                <Fragment>
                                    <StyledUsersIcon />
                                    <StyledSelectInput
                                        source="newChild.id"
                                        choices={availableChildren.map((child) => ({
                                            id: child.id,
                                            name: `${child.name} ${child.surname}`
                                        }))}
                                        label={false}
                                    />
                                    <ShsToggle source={`newChild.online`} label="Онлайн" />
                                    <ChildItemControlsBox>
                                        <StyledEditActionButton
                                            styleType=""
                                            label=""
                                            icon={<Check />}
                                            onClick={() => handleChildAdd()}
                                            sx={{
                                                color: '#22AD5C',
                                                backgroundColor: '#F1FCF5',
                                                ':hover': {
                                                    backgroundColor: '#F1FCF5'
                                                }
                                            }}
                                            disabled={!newChildId}
                                        />
                                        <StyledEditActionButton
                                            styleType=""
                                            label=""
                                            icon={<CloseIcon />}
                                            onClick={() => {
                                                dialogForm.resetField('newChild');
                                                setAddChildMode(false);
                                            }}
                                            sx={{
                                                color: '#F23030',
                                                backgroundColor: '#FEF3F3',
                                                ':hover': {
                                                    backgroundColor: '#FEF3F3'
                                                }
                                            }}
                                        />
                                    </ChildItemControlsBox>
                                </Fragment>
                            ) : (
                                <NewChildGrid>
                                    <ShsButton
                                        styleType="outline-no-border"
                                        label="Додати учня"
                                        icon={<AddCircleOutlineIcon />}
                                        onClick={() => setAddChildMode(true)}
                                        disabled={availableChildren.length === 0}
                                        sx={{ padding: 0 }}
                                    />
                                </NewChildGrid>
                            )}
                        </ChildrenGrid>
                        <ShsSelectInput
                            source="classroomId"
                            label="Приміщення уроку"
                            choices={[
                                ...availableClassrooms,
                                { id: 'other', name: 'Інше' } // Selecting this option will result in classroomId: null being sent to the backend
                            ]}
                            required={isChildrenOffline}
                            disabled={!isChildrenOffline}
                        />
                        <ShsSelectInput
                            source="employeeId"
                            label="Викладач"
                            choices={
                                employeesData?.map((employee) => ({
                                    id: employee.id,
                                    name: `${employee.name} ${employee.surname}`
                                })) || []
                            }
                            required
                        />
                        <ShsTextInput
                            source="meetingLink"
                            label="Посилання на онлайн-урок"
                            validate={regex(URL_REGEX, INVALID_URL_MESSAGE)}
                            required={isChildrenOnline}
                        />
                        <ShsTextInput source="notes" label="Нотатки" inputProps={{ maxLength: notesMaxLength }} />
                    </FormProvider>
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <ShsButton onClick={onSaveClick} label="Зберегти" disabled={addChildMode || !isDirty || !isValid} />
                <ShsButton onClick={onClose} styleType="outline" label="Скасувати" />
                <WithRole roles={[UserRole.SYSTEM_ADMIN, UserRole.ADMIN, UserRole.METHODOLOGIST]}>
                    <ShsButton
                        onClick={() => handleLessonDelete(dialogEvent!)}
                        styleType="outline-danger"
                        label="Видалити"
                        icon={<DeleteIcon fill={palette.error.main} />}
                        disabled={!ScheduleManager.isCalendarEventEdit()}
                    />
                </WithRole>
            </DialogActions>
        </StyledDialog>
    );
};
